import Screen from "../../components/Screen";
import { useDevice } from "../../hooks/useDevice";
import {
  BackButtonWrapper,
  CreateButtonWrapper,
  DeviceTwinHierarchyWrapper,
  EdgeTileContainer,
  EmptyTile,
  HeaderDetailTile,
  HeaderTiles,
  ListTile,
  ScreenHeader,
  SpinnerWrapper,
  TileWrapper,
} from "./DevicePage.styles";
import { useNavigate, useParams, useSearchParams } from "react-router";
import BreadCrumb from "../../components/Breadcrumb";
import {
  ArrowNavigationLeftOutlined,
  SettingsOutlined,
} from "@gds-react/icons";
import { Button, Icon, Tab, TabList, TabPanel, Tabs } from "@gds-react/core";
import { routes } from "../../utils/routeHelper";
import CreateDeviceButton from "../../components/CreateDeviceButton";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  DevicePropertiesBaseCommunicationDeviceProperties,
  Meta,
} from "../../generated-api/device";
import { useTranslation } from "react-i18next";
import DeviceListTab from "./Tabs/DeviceListTab";
import { DeviceTabTypeEnum } from "../../types/DeviceTabTypeEnum";
import InsightsTab from "./Tabs/InsightsTab";
import { Spinner } from "../LoadingPage/LoadingPage.styles";
import UnassignedProductsBanner from "./UnassignedProductsBanner/UnassignedProductsBanner";
import { useStore } from "../../store/store";
import AlertSettingsTab from "./Tabs/AlertSettingsTab";
import { getCallbackUrl } from "../../utils/helpers/getCallbackUrl";
import {
  getBreadcrumbLink,
  getPreviousNavigableBreadcrumb,
} from "../../components/Breadcrumb/Breadcrumb.helpers";
import { CoreWidgetTypeEnum } from "@gf-core/widgets/types/CoreWidgetTypeEnum";
import { CoreDeviceListRef } from "@gf-core/widgets/types/CoreDeviceListRef";
import CoreDeviceDetailViewer from "@gf-core/widgets/widgets/CoreDeviceDetailViewer";
import CoreDeviceRule from "@gf-core/widgets/widgets/CoreDeviceRule";
import CoreDeviceTwinViewer from "@gf-core/widgets/widgets/CoreDeviceTwinViewer";
import CoreDeviceContextMenu from "@gf-core/widgets/widgets/CoreDeviceContextMenu";
import CoreDeviceHealthScore from "@gf-core/widgets/widgets/CoreDeviceHealthScore";
import { CoreContextMenuActionsEnum } from "@gf-core/widgets/types/CoreContextMenuActionsEnum";
import CoreNetworkQuality from "@gf-core/widgets/widgets/CoreNetworkQuality";
import CoreLanConnection from "@gf-core/widgets/widgets/CoreLanConnection";
import CoreEdgeDeviceTiles from "@gf-core/widgets/widgets/CoreEdgeDeviceTiles";
import { DeviceBase } from "@gf-core/widgets/types/device";
import AlertsHistoryTab from "./Tabs/AlertHistoryTab";
import ActiveAlertTab from "./Tabs/ActiveAlertTab";
import ReportedStateTab from "./Tabs/ReportedStateTab";
import RulesTab from "./Tabs/RulesTab";
import {
  SubscriptionStatus,
  SubscriptionStatusWidget,
} from "@gf-ww-service-portal/widgets";

interface ContextMenuProps {
  device: DeviceBase;
  installationId?: string;
  onSuccess?: (widgetType: CoreWidgetTypeEnum) => void;
  onWidgetClose: () => void;
}

function ContextMenu({
  device,
  installationId,
  onSuccess,
  onWidgetClose,
}: ContextMenuProps) {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [showContextMenu, setShowContextMenu] = useState(false);
  const { t } = useTranslation();
  const showAppStoreFeature = useStore.useShowAppStoreFeature();

  return (
    <CoreDeviceContextMenu
      device={device as unknown as DeviceBase}
      deviceTwinId={device.deviceTwinId}
      installationId={installationId}
      show={showContextMenu}
      accountId={showAppStoreFeature ? accountId : undefined}
      onRedirectToAppStore={
        showAppStoreFeature
          ? (productId: string) => navigate(routes.product.set(productId))
          : undefined
      }
      onClose={() => {
        setShowContextMenu(false);
      }}
      onWidgetClose={onWidgetClose}
      onSuccess={onSuccess}
      wibutlerCallbackUrl={getCallbackUrl()}
      activeMenuActions={[
        CoreContextMenuActionsEnum.CHANGE_CONFIGURATION,
        CoreContextMenuActionsEnum.DELETE_DEVICE,
        CoreContextMenuActionsEnum.MODBUS_SETTINGS,
        CoreContextMenuActionsEnum.REDISCOVER,
        CoreContextMenuActionsEnum.REPLACE_GATEWAY,
        CoreContextMenuActionsEnum.UPDATE_DEVICE,
        CoreContextMenuActionsEnum.SHOW_DEVICE_RULE,
        CoreContextMenuActionsEnum.SHOW_REPROFILE,
        CoreContextMenuActionsEnum.RESET_SOFTWARE,
      ]}
    >
      <Button
        onClick={() => {
          setShowContextMenu(true);
        }}
      >
        <Icon src={<SettingsOutlined />} />
        {t("devicePage.actionButton")}
      </Button>
    </CoreDeviceContextMenu>
  );
}

export default function DevicePage() {
  const { t } = useTranslation();
  const deviceListRef = useRef<CoreDeviceListRef>(null);
  const { installationId, accountId, deviceId, tabIndex } = useParams();
  const {
    data: device,
    isLoading: isLoadingDevice,
    refetch: refetchDevice,
  } = useDevice(installationId ?? "", deviceId ?? "", true);
  const navigate = useNavigate();
  const [ruleState, setRuleState] = useState<{
    authenticationCode: string | undefined;
    installationId: string | undefined;
    deviceTwinId: string | undefined;
  }>({
    authenticationCode: undefined,
    installationId: undefined,
    deviceTwinId: undefined,
  });
  const [showRuleWidget, setShowRuleWidget] = useState(false);
  const [showWibutlerRuleWidget, setShowWibutlerRuleWidget] = useState(false);
  const showAppStoreFeature = useStore.useShowAppStoreFeature();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  const supportsAndHasSubdevices =
    device?.model.supportsSubDevices || device?.model.hasSubDevices;
  const setActiveMenuId = useStore.useSetActiveMenuId();
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");

  useEffect(() => {
    setActiveMenuId(deviceId);
  }, [deviceId]);

  const isSupportingHealthScore = useMemo(() => {
    const type = device?.model?.$deviceType;
    return type === "GdpCommunicationDevice" || type === "MoxaDevice";
  }, [device]);

  const selectedTabValue =
    !supportsAndHasSubdevices &&
    (tabIndex as DeviceTabTypeEnum) === DeviceTabTypeEnum.Overview
      ? DeviceTabTypeEnum.ActiveAlerts
      : ((tabIndex as DeviceTabTypeEnum) ?? DeviceTabTypeEnum.Overview);

  const onBack = () => {
    const breadcrumbs = (device?.meta as Meta)?.breadcrumbs ?? [];
    const previousItem = getPreviousNavigableBreadcrumb(breadcrumbs);
    const link =
      previousItem &&
      getBreadcrumbLink(previousItem, {
        accountId: accountId ?? "",
        installationId: installationId ?? "",
        deviceTab: tabIndex as DeviceTabTypeEnum,
      });

    navigate(
      link ?? routes.installation.set(accountId ?? "", installationId ?? ""),
    );
  };

  const addressType = useMemo(() => {
    const addressType = (
      device?.model
        .properties as DevicePropertiesBaseCommunicationDeviceProperties
    )?.addressType;
    return addressType;
  }, [device]);

  useEffect(() => {
    if (action === "core-device-widget" && !isLoadingDevice) {
      setTimeout(() => {
        setShowRuleWidget(true);
      }, 500);
    }
  }, [action, isLoadingDevice]);

  useEffect(() => {
    if (code && state && !isLoadingDevice) {
      const data = localStorage.getItem("@gf-core/widgets");
      const parsedData = data ? JSON.parse(data) : null;
      const rules = parsedData ? parsedData.state.wibutlerRules : {};
      const rule = state && rules ? rules[state] : null;

      setRuleState({
        authenticationCode: code,
        deviceTwinId: rule?.deviceTwinId,
        installationId: rule?.installationId,
      });
      setShowWibutlerRuleWidget(true);
    }
  }, [code, state, isLoadingDevice]);

  const extraLines = useMemo(() => {
    if (device?.model.$deviceType === "GdpCommunicationDevice") {
      return [
        {
          label: t("devicePage.subscriptionStatus"),
          value: (
            <SubscriptionStatusWidget
              accountId={accountId ?? ""}
              deviceTwinId={deviceId ?? ""}
            >
              <SubscriptionStatus showTooltip showSubscriptionDetails />
            </SubscriptionStatusWidget>
          ),
        },
      ];
    }
    return [];
  }, [device, accountId, deviceId]);

  return (
    <>
      <Screen id={deviceId}>
        <ScreenHeader>
          <BackButtonWrapper>
            <Button onClick={onBack}>
              <Icon src={<ArrowNavigationLeftOutlined />} />
              {t("devicePage.backButton")}
            </Button>
          </BackButtonWrapper>

          <BreadCrumb
            list={(device?.meta as Meta)?.breadcrumbs ?? []}
            isLoading={isLoadingDevice}
          />

          {device && (
            <ContextMenu
              device={device.model as unknown as DeviceBase}
              installationId={installationId}
              onWidgetClose={() => {
                refetchDevice();
                deviceListRef.current?.refetch();
              }}
              onSuccess={(widgetType) => {
                if (widgetType === CoreWidgetTypeEnum.CoreDeviceDelete) {
                  onBack();
                }
              }}
            />
          )}
        </ScreenHeader>
        <HeaderTiles>
          {installationId && deviceId && (
            <HeaderDetailTile>
              <CoreDeviceDetailViewer
                onDeviceUpdated={() => {
                  refetchDevice();
                }}
                installationId={installationId}
                deviceTwinId={deviceId}
                extraLines={extraLines}
              />
            </HeaderDetailTile>
          )}

          {isSupportingHealthScore ? (
            <TileWrapper>
              <CoreDeviceHealthScore
                installationId={installationId ?? ""}
                deviceTwinId={deviceId ?? ""}
              />

              {addressType === "Imei" ? (
                <CoreNetworkQuality
                  installationId={installationId ?? ""}
                  deviceTwinId={deviceId ?? ""}
                />
              ) : addressType === "Mac" ? (
                <CoreLanConnection
                  installationId={installationId ?? ""}
                  deviceTwinId={deviceId ?? ""}
                />
              ) : (
                <EmptyTile />
              )}
            </TileWrapper>
          ) : (
            <DeviceTwinHierarchyWrapper>
              <CoreDeviceTwinViewer
                deviceTwinId={deviceId ?? ""}
                isModal={false}
                onNavigate={(
                  accountId: string,
                  installationId: string,
                  deviceId?: string,
                ) => {
                  if (deviceId) {
                    navigate(
                      routes.device.set(
                        accountId,
                        installationId,
                        deviceId,
                        tabIndex as DeviceTabTypeEnum,
                      ),
                    );
                  } else {
                    navigate(
                      routes.installation.set(accountId, installationId),
                    );
                  }
                }}
              />
            </DeviceTwinHierarchyWrapper>
          )}
        </HeaderTiles>

        {showAppStoreFeature && (
          <UnassignedProductsBanner
            accountId={accountId}
            onSetupConnector={() => setShowRuleWidget(true)}
          />
        )}

        {device?.model.$deviceType === "MoxaDevice" &&
          installationId &&
          device?.model.iotDeviceId && (
            <EdgeTileContainer>
              <CoreEdgeDeviceTiles
                installationId={installationId}
                iotDeviceId={device?.model.iotDeviceId}
              />
            </EdgeTileContainer>
          )}

        {isLoadingDevice && (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        )}
        {!isLoadingDevice && (
          <ListTile>
            {device?.model.supportsSubDevices &&
              (selectedTabValue === DeviceTabTypeEnum.Overview ||
                !tabIndex) && (
                <CreateButtonWrapper>
                  <CreateDeviceButton
                    installationId={installationId}
                    device={device?.model}
                    onSuccess={() => {
                      if (deviceListRef.current) {
                        deviceListRef.current.refetch();
                      }
                    }}
                  />
                </CreateButtonWrapper>
              )}

            {supportsAndHasSubdevices ? (
              <Tabs
                value={selectedTabValue}
                onValueChange={(value) => {
                  navigate(
                    routes.device.set(
                      accountId ?? "",
                      installationId ?? "",
                      deviceId ?? "",
                      value as DeviceTabTypeEnum,
                    ),
                    { preventScrollReset: true },
                  );
                }}
              >
                <TabList>
                  <Tab value={DeviceTabTypeEnum.Overview}>
                    {t("devicePage.tabs.devices")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.Insights}>
                    {t("devicePage.tabs.insights")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.ActiveAlerts}>
                    {t("devicePage.tabs.activeAlerts")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.AlertHistory}>
                    {t("devicePage.tabs.alertHistory")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.AlertSettings}>
                    {t("devicePage.tabs.alertSettings")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.ReportedState}>
                    {t("devicePage.tabs.reportedState")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.Rules}>
                    {t("devicePage.tabs.rules")}
                  </Tab>
                </TabList>
                <TabPanel value={DeviceTabTypeEnum.Overview}>
                  <DeviceListTab ref={deviceListRef} />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.Insights}>
                  <InsightsTab />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.ActiveAlerts}>
                  <ActiveAlertTab />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.AlertHistory}>
                  <AlertsHistoryTab />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.AlertSettings}>
                  <AlertSettingsTab />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.ReportedState}>
                  <ReportedStateTab />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.Rules}>
                  <RulesTab />
                </TabPanel>
              </Tabs>
            ) : (
              <Tabs
                value={selectedTabValue}
                onValueChange={(value) => {
                  navigate(
                    routes.device.set(
                      accountId ?? "",
                      installationId ?? "",
                      deviceId ?? "",
                      value as DeviceTabTypeEnum,
                    ),
                    { preventScrollReset: true },
                  );
                }}
              >
                <TabList>
                  <Tab value={DeviceTabTypeEnum.ActiveAlerts}>
                    {t("devicePage.tabs.activeAlerts")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.AlertHistory}>
                    {t("devicePage.tabs.alertHistory")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.AlertSettings}>
                    {t("devicePage.tabs.alertSettings")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.ReportedState}>
                    {t("devicePage.tabs.reportedState")}
                  </Tab>
                  <Tab value={DeviceTabTypeEnum.Rules}>
                    {t("devicePage.tabs.rules")}
                  </Tab>
                </TabList>
                <TabPanel value={DeviceTabTypeEnum.ActiveAlerts}>
                  <ActiveAlertTab />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.AlertHistory}>
                  <AlertsHistoryTab />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.AlertSettings}>
                  <AlertSettingsTab />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.ReportedState}>
                  <ReportedStateTab />
                </TabPanel>
                <TabPanel value={DeviceTabTypeEnum.Rules}>
                  <RulesTab />
                </TabPanel>
              </Tabs>
            )}
          </ListTile>
        )}

        <CoreDeviceRule
          show={showRuleWidget}
          onClose={() => setShowRuleWidget(false)}
          installationId={installationId ?? ""}
          deviceTwinId={deviceId ?? ""}
          accountId={accountId}
          onRedirectToAppStore={(productId: string) =>
            navigate(routes.product.set(productId))
          }
        />

        {ruleState.authenticationCode &&
          ruleState.deviceTwinId &&
          ruleState.installationId && (
            <CoreDeviceRule
              show={showWibutlerRuleWidget}
              wibutlerAuthorizationCode={ruleState.authenticationCode}
              wibutlerCallbackUrl={getCallbackUrl()}
              deviceTwinId={ruleState.deviceTwinId}
              installationId={ruleState.installationId}
              onRedirectToAppStore={(productId: string) =>
                navigate(routes.product.set(productId))
              }
              onClose={() => setShowWibutlerRuleWidget(false)}
            />
          )}
      </Screen>
    </>
  );
}
